"use client"

import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps'
import { Button } from '@/shared/components/ui/button'
import { X, MapPin } from 'lucide-react'
import FormTextareaInput from './form-textarea-input'
import { useFormContext, useWatch } from 'react-hook-form'
import { authFetchJson } from '@/shared/lib/auth-fetch'

type LocationDetails = {
  city: string
  state: string
  country: string
  latitude?: number
  longitude?: number
  zipCode?: string
}

interface MapPinDropProps<T> {
  /** Called only when the user presses "Use This Location" with a valid pin and description. */
  onLocationSelected: (location: LocationDetails) => void
  onClose: () => void
  control: any
  fieldPrefix?: string
}

function parseCoord(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null
  const n = typeof value === "number" ? value : parseFloat(String(value))
  return Number.isFinite(n) ? n : null
}

function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371000
  const φ1 = (a.lat * Math.PI) / 180
  const φ2 = (b.lat * Math.PI) / 180
  const Δφ = ((b.lat - a.lat) * Math.PI) / 180
  const Δλ = ((b.lng - a.lng) * Math.PI) / 180
  const s =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s))
}

function MapPinDropContent<T extends Record<string, any>>({
  onLocationSelected,
  onClose,
  control,
  fieldPrefix = "",
}: MapPinDropProps<T>) {
  const { setValue, getValues } = useFormContext<T>()
  const map = useMap()
  const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number } | null>(null)
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false)
  const [reverseGeocodedAddress, setReverseGeocodedAddress] = useState<string>("")
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null)
  const [isLoadingRegion, setIsLoadingRegion] = useState(false)
  const [describeHomeError, setDescribeHomeError] = useState<string | null>(null)
  const [userLocation, setUserLocation] = useState<{
    lat: number
    lng: number
    heading: number | null
  } | null>(null)
  const [mapZoom, setMapZoom] = useState<number | null>(null)
  const geocoderRef = useRef<google.maps.Geocoder | null>(null)
  const lastMapLocationRef = useRef<LocationDetails | null>(null)
  const reverseGeocodeFromPositionRef = useRef<
    (
      position: { lat: number; lng: number },
      options?: { markFormDirty?: boolean },
    ) => void | Promise<void>
  >(async () => {})
  const didAutoFitBoundsRef = useRef(false)

  const getFieldPath = (field: string) =>
    fieldPrefix ? `${fieldPrefix}.home.${field}` : `home.${field}`

  const addressFieldPath = getFieldPath("address") as any
  // `name` is dynamic so useWatch's value type is not inferred as string; treat as unknown for trimming.
  const watchedAddress = useWatch({ control, name: addressFieldPath }) as unknown

  useEffect(() => {
    const text = typeof watchedAddress === "string" ? watchedAddress : ""
    if (text.trim().length > 0) {
      setDescribeHomeError(null)
    }
  }, [watchedAddress])

  // Initialize geocoder
  useEffect(() => {
    if (typeof window !== 'undefined' && window.google?.maps) {
      geocoderRef.current = new google.maps.Geocoder()
    }
  }, [])

  const reverseGeocodeFromPosition = useCallback(
    async (
      position: { lat: number; lng: number },
      options?: { markFormDirty?: boolean },
    ) => {
      if (!geocoderRef.current) return

      const markFormDirty = options?.markFormDirty !== false
      const dirtyOpts = { shouldDirty: markFormDirty as boolean }

      setIsReverseGeocoding(true)
      const getPath = (field: string) =>
        fieldPrefix ? `${fieldPrefix}.home.${field}` : `home.${field}`

      try {
        const results = await geocoderRef.current.geocode({
          location: position,
        })

        if (results && results.results && results.results.length > 0) {
          const place = results.results[0]
          const approx = place.formatted_address || ""
          setReverseGeocodedAddress(approx)

          const components = place.address_components || []
          let city = ""
          let state = ""
          let country = ""
          let zipCode = ""

          components.forEach((component) => {
            const types = component.types
            if (types.includes("locality")) city = component.long_name
            if (types.includes("administrative_area_level_1"))
              state = component.long_name
            if (types.includes("country")) country = component.long_name
            if (types.includes("postal_code")) zipCode = component.long_name
          })

          // Do not write Google's formatted string to home.address — user describes the home there.
          setValue(getPath("city") as any, city as any, dirtyOpts)
          setValue(getPath("state") as any, state as any, dirtyOpts)
          setValue(getPath("country") as any, country as any, dirtyOpts)
          setValue(getPath("zipCode") as any, zipCode as any, dirtyOpts)
          setValue(getPath("latitude") as any, position.lat as any, dirtyOpts)
          setValue(getPath("longitude") as any, position.lng as any, dirtyOpts)

          lastMapLocationRef.current = {
            city,
            state,
            country,
            latitude: position.lat,
            longitude: position.lng,
            zipCode,
          }
        } else {
          setReverseGeocodedAddress("Location selected (address not available)")
          setValue(getPath("latitude") as any, position.lat as any, dirtyOpts)
          setValue(getPath("longitude") as any, position.lng as any, dirtyOpts)
          lastMapLocationRef.current = {
            city: "",
            state: "",
            country: "",
            latitude: position.lat,
            longitude: position.lng,
            zipCode: "",
          }
        }
      } catch (error) {
        console.error('Reverse geocoding error:', error)
        setReverseGeocodedAddress("Location selected (address not available)")
        setValue(getPath("latitude") as any, position.lat as any, dirtyOpts)
        setValue(getPath("longitude") as any, position.lng as any, dirtyOpts)
        lastMapLocationRef.current = {
          city: "",
          state: "",
          country: "",
          latitude: position.lat,
          longitude: position.lng,
          zipCode: "",
        }
      } finally {
        setIsReverseGeocoding(false)
      }
    },
    [setValue, fieldPrefix]
  )

  reverseGeocodeFromPositionRef.current = reverseGeocodeFromPosition

  const handleMapClick = useCallback(
    (e: any) => {
      const latLng = e.detail?.latLng || e.latLng
      if (latLng) {
        const position = {
          lat: typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat,
          lng: typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng,
        }
        setMarkerPosition(position)
        void reverseGeocodeFromPosition(position)
      }
    },
    [reverseGeocodeFromPosition]
  )

  // One-time: saved home pin takes priority; otherwise GPS/region for initial pin (map view synced below).
  useEffect(() => {
    const getPath = (field: string) =>
      fieldPrefix ? `${fieldPrefix}.home.${field}` : `home.${field}`

    const lat = parseCoord(getValues(getPath("latitude") as any))
    const lng = parseCoord(getValues(getPath("longitude") as any))

    if (lat !== null && lng !== null) {
      const saved = { lat, lng }
      setMarkerPosition(saved)
      setMapCenter(saved)
      setMapZoom(15)

      const city = String(getValues(getPath("city") as any) ?? "")
      const state = String(getValues(getPath("state") as any) ?? "")
      const country = String(getValues(getPath("country") as any) ?? "")
      const zipCode = String(getValues(getPath("zipCode") as any) ?? "")
      lastMapLocationRef.current = {
        city,
        state,
        country,
        latitude: lat,
        longitude: lng,
        zipCode,
      }

      setTimeout(() => {
        if (geocoderRef.current) {
          void reverseGeocodeFromPositionRef.current(saved, {
            markFormDirty: false,
          })
        }
      }, 0)
      return
    }

    const fetchRegionData = async (regionId: string) => {
      setIsLoadingRegion(true)
      try {
        const res = await authFetchJson<any>(`/regions/${regionId}`)
        if (res.success && res.data) {
          const region = res.data

          if (region?.baseLatitude && region?.baseLongitude) {
            const regionCenter = {
              lat:
                typeof region.baseLatitude === "string"
                  ? parseFloat(region.baseLatitude)
                  : region.baseLatitude,
              lng:
                typeof region.baseLongitude === "string"
                  ? parseFloat(region.baseLongitude)
                  : region.baseLongitude,
            }

            if (!Number.isNaN(regionCenter.lat) && !Number.isNaN(regionCenter.lng)) {
              setMapCenter(regionCenter)
              setMapZoom(12)
              setIsLoadingRegion(false)
              return
            }
          }
        }
      } catch (error) {
        console.error("Error fetching region data:", error)
      } finally {
        setIsLoadingRegion(false)
      }

      const jamaicaCenter = { lat: 18.1096, lng: -77.2975 }
      setMapCenter(jamaicaCenter)
      setMapZoom(12)
    }

    const fetchRegionBaseLocation = async () => {
      const urlParams = new URLSearchParams(window.location.search)
      const urlRegion = urlParams.get("region")
      const sessionRegion =
        sessionStorage.getItem("unifiedAuthRegion") ||
        sessionStorage.getItem("registrationRegion")
      const regionId = urlRegion || sessionRegion
      if (regionId) {
        await fetchRegionData(regionId)
      } else {
        await fetchRegionData("4")
      }
    }

    const determineMapCenter = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const pin = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            }
            setMapCenter(pin)
            setMarkerPosition(pin)
            setMapZoom(15)
            setTimeout(() => {
              if (geocoderRef.current) {
                void reverseGeocodeFromPositionRef.current(pin)
              }
            }, 0)
          },
          async (error) => {
            console.warn("Geolocation error:", error)
            await fetchRegionBaseLocation()
          },
        )
      } else {
        void fetchRegionBaseLocation()
      }
    }

    void determineMapCenter()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: initialize once from form / region
  }, [])

  // Apply center/zoom when the map instance is ready.
  useEffect(() => {
    if (!map || !mapCenter) return
    map.setCenter(mapCenter)
    if (mapZoom != null) {
      map.setZoom(mapZoom)
    }
  }, [map, mapCenter, mapZoom])

  // Continuous device location (blue dot). Kept separate from the draggable home pin.
  useEffect(() => {
    if (!navigator.geolocation) return
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const h = pos.coords.heading
        const heading =
          h != null && Number.isFinite(h) && h >= 0 && h <= 360 ? h : null
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          heading,
        })
      },
      () => {
        setUserLocation(null)
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 },
    )
    return () => {
      navigator.geolocation.clearWatch(watchId)
    }
  }, [])

  // When the saved pin and the user are far apart, fit both in view (once).
  useEffect(() => {
    if (!map || !markerPosition || !userLocation || didAutoFitBoundsRef.current) return
    if (haversineMeters(markerPosition, userLocation) < 35) return
    didAutoFitBoundsRef.current = true
    const bounds = new google.maps.LatLngBounds()
    bounds.extend(markerPosition)
    bounds.extend(userLocation)
    map.fitBounds(bounds, 72)
  }, [map, markerPosition, userLocation])

  const handleUseThisLocation = () => {
    if (!markerPosition) return

    const rawAddress = getValues(addressFieldPath)
    const trimmed =
      typeof rawAddress === "string" ? rawAddress.trim() : String(rawAddress ?? "").trim()

    if (!trimmed) {
      setDescribeHomeError(
        "Please enter a brief description of how to find the home.",
      )
      return
    }

    const loc = lastMapLocationRef.current
    if (
      !loc ||
      loc.latitude === undefined ||
      loc.longitude === undefined
    ) {
      setDescribeHomeError(
        "Could not read coordinates for this pin. Try moving the pin slightly and wait for the map to finish loading.",
      )
      return
    }

    setDescribeHomeError(null)
    onLocationSelected(loc)
  }

  const showUserLocationDot = Boolean(
    userLocation &&
      markerPosition &&
      haversineMeters(markerPosition, userLocation) > 25,
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Select Location on Map</h3>
          <p className="text-sm text-muted-foreground">
            The red pin is the home location (drag it or click the map to move it). When location
            permission is on, the blue dot is where you are now so you can line the pin up with the
            property.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-8 w-8"
          showLoader={false}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="relative">
        {isLoadingRegion && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 rounded-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading map...</p>
            </div>
          </div>
        )}
        <Map
          defaultCenter={mapCenter || { lat: 18.1096, lng: -77.2975 }}
          defaultZoom={12}
          mapTypeId="roadmap"
          onClick={handleMapClick}
          className="h-[400px] w-full rounded-lg border"
          mapId="PIN_DROP_MAP_ID"
        >
          {showUserLocationDot && userLocation && (
            <AdvancedMarker
              position={{ lat: userLocation.lat, lng: userLocation.lng }}
              draggable={false}
            >
              <div
                className="pointer-events-none flex h-12 w-12 items-end justify-center"
                title="Your location"
              >
                <div
                  className="relative flex flex-col items-center justify-end"
                  style={
                    userLocation.heading != null
                      ? {
                          transform: `rotate(${userLocation.heading}deg)`,
                          transformOrigin: "50% 100%",
                        }
                      : undefined
                  }
                >
                  {userLocation.heading != null && (
                    <div
                      className="-mb-0.5 h-0 w-0 border-x-[11px] border-b-[26px] border-x-transparent border-b-[rgba(66,133,244,0.45)]"
                      aria-hidden
                    />
                  )}
                  <div className="relative z-[1] h-3.5 w-3.5 shrink-0 rounded-full border-2 border-white bg-[#1a73e8] shadow-md ring-2 ring-[#1a73e8]/40" />
                </div>
              </div>
            </AdvancedMarker>
          )}
          {markerPosition && (
            <AdvancedMarker
              position={markerPosition}
              draggable={true}
              onDragEnd={(e) => {
                if (e.latLng) {
                  const lat = typeof e.latLng.lat === 'function' ? e.latLng.lat() : e.latLng.lat
                  const lng = typeof e.latLng.lng === 'function' ? e.latLng.lng() : e.latLng.lng
                  const position: { lat: number; lng: number } = { 
                    lat: typeof lat === 'number' ? lat : lat(), 
                    lng: typeof lng === 'number' ? lng : lng() 
                  }
                  setMarkerPosition(position)
                  void reverseGeocodeFromPosition(position)
                }
              }}
            >
              <div className="flex items-center justify-center cursor-move">
                <MapPin className="h-8 w-8 text-red-600 drop-shadow-lg" fill="currentColor" />
              </div>
            </AdvancedMarker>
          )}
        </Map>
      </div>

      {isReverseGeocoding && (
        <div className="text-sm text-muted-foreground">
          Getting location details...
        </div>
      )}

      {reverseGeocodedAddress && !isReverseGeocoding && (
        <div className="space-y-2">
          <div>
            <label className="text-sm font-medium">Approximate map location (for your reference only)</label>
            <p className="text-sm text-muted-foreground">{reverseGeocodedAddress}</p>
          </div>
        </div>
      )}

      {markerPosition && (
        <div className="space-y-2">
          <FormTextareaInput
            inputGroupProps={{
              label: "Describe Home *",
            }}
            placeholder="Brief description of how to find the home (this is saved as the home address)"
            name={addressFieldPath}
            control={control}
            rows={3}
          />
          {describeHomeError && (
            <p className="text-sm text-destructive" role="alert">
              {describeHomeError}
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose} showLoader={false}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleUseThisLocation}
          disabled={!markerPosition}
          showLoader={false}
        >
          Use This Location
        </Button>
      </div>
    </div>
  )
}

export function MapPinDrop<T extends Record<string, any>>(props: MapPinDropProps<T>) {
  return <MapPinDropContent {...props} />
}
